import {
  Box,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SimpleShowLayout, Title } from 'react-admin';
import { useGetAppConfig } from './useGetAppConfig';
import { BlockerContentUpdateForm } from './BlockerContentUpdateForm';
import { TrialDurationUpdateForm } from './TrialDurationUpdateForm';
import { MandatoryOTAForm } from './MandatoryOTAForm';
import { SupportedNativeVersionRange } from './SupportedNativeVersionRange';
import { CalendarIntegration } from './CalendarIntegration';
import { AudioChunkSize } from './AudioChunkSize';
import { QuickFacts } from './QuickFacts';
import { NotePreferences } from './NotePreferences';
import { StripeWebSubscription } from './StripeWebSubscription';
import { FreeTrialPopupPlacement } from './FreeTrialPopupPlacement';
import { AppReviewRequestPlacement } from './AppReviewRequestPlacement';
import { TracingSampleRates } from './TracingSampleRates';
import { AppleSsoMigrationHack } from './AppleSsoMigrationHack';
import { PromptForDetailsIfMissingForApple } from './PromptForDetailsIfMissingForApple';
import { ModernAgentPrompts } from './ModernAgentPrompts';
import { PromptAddClientPronounsAfterSessionNote } from './PromptAddClientPronounsAfterSessionNote';
import { InsuranceCompanyFilter } from './InsuranceCompanyFilter';
import { MagicEdit } from './MagicEdit';
import { WebSessionNoteActions } from './WebSessionNoteActions';
import { CustomReports } from './CustomReports';
import { CustomPromptAudioSupport } from './CustomPromptAudioSupport';
import { DefaultModelId } from './DefaultModelId';
import { DefaultTranscriptModelId } from './DefaultTranscriptModelId';

export const TherapistAppConfig = () => {
  const { remoteAppConfig } = useGetAppConfig();

  if (!remoteAppConfig) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        paddingTop="6rem"
        alignItems="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <SimpleShowLayout record={remoteAppConfig}>
      <Title title="Therapist App - Configuration" />

      {/* System Controls */}
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            flexDirection: 'row-reverse',
            '& .MuiAccordionSummary-expandIconWrapper': {
              transform: 'rotate(-90deg)',
            },
            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
              transform: 'rotate(0deg)',
            },
          }}
        >
          <Typography variant="h6" sx={{ marginLeft: '1em' }}>
            System Controls
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            padding: '1rem',
          }}
        >
          <MandatoryOTAForm />
          <SupportedNativeVersionRange />
        </AccordionDetails>
      </Accordion>

      {/* Trial & Onboarding */}
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            flexDirection: 'row-reverse',
            '& .MuiAccordionSummary-expandIconWrapper': {
              transform: 'rotate(-90deg)',
            },
            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
              transform: 'rotate(0deg)',
            },
          }}
        >
          <Typography variant="h6" sx={{ marginLeft: '1em' }}>
            Trial & Onboarding
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            padding: '1rem',
          }}
        >
          <TrialDurationUpdateForm />
          <FreeTrialPopupPlacement />
          <AppReviewRequestPlacement />
        </AccordionDetails>
      </Accordion>

      {/* Stabilized Features */}
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            flexDirection: 'row-reverse',
            '& .MuiAccordionSummary-expandIconWrapper': {
              transform: 'rotate(-90deg)',
            },
            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
              transform: 'rotate(0deg)',
            },
          }}
        >
          <Typography variant="h6" sx={{ marginLeft: '1em' }}>
            Stabilized Features
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            padding: '1rem',
          }}
        >
          <CalendarIntegration />
          <QuickFacts />
          <ModernAgentPrompts />
          <PromptAddClientPronounsAfterSessionNote />
          <InsuranceCompanyFilter />
          <MagicEdit />
          <WebSessionNoteActions />
          <CustomReports />
          <CustomPromptAudioSupport />
          <NotePreferences />
          <StripeWebSubscription />
        </AccordionDetails>
      </Accordion>

      {/* Migration & Support */}
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            flexDirection: 'row-reverse',
            '& .MuiAccordionSummary-expandIconWrapper': {
              transform: 'rotate(-90deg)',
            },
            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
              transform: 'rotate(0deg)',
            },
          }}
        >
          <Typography variant="h6" sx={{ marginLeft: '1em' }}>
            Migration & Support
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            padding: '1rem',
          }}
        >
          <AppleSsoMigrationHack />
          <PromptForDetailsIfMissingForApple />
        </AccordionDetails>
      </Accordion>

      {/* Technical Settings */}
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            flexDirection: 'row-reverse',
            '& .MuiAccordionSummary-expandIconWrapper': {
              transform: 'rotate(-90deg)',
            },
            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
              transform: 'rotate(0deg)',
            },
          }}
        >
          <Typography variant="h6" sx={{ marginLeft: '1em' }}>
            Technical Settings
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            padding: '1rem',
          }}
        >
          <DefaultModelId />
          <DefaultTranscriptModelId />
          <AudioChunkSize />
          <TracingSampleRates />
        </AccordionDetails>
      </Accordion>

      {/* Global Blocker */}
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            flexDirection: 'row-reverse',
            '& .MuiAccordionSummary-expandIconWrapper': {
              transform: 'rotate(-90deg)',
            },
            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
              transform: 'rotate(0deg)',
            },
          }}
        >
          <Typography variant="h6" sx={{ marginLeft: '1em' }}>
            Global Blocker
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            padding: '1rem',
          }}
        >
          <BlockerContentUpdateForm />
        </AccordionDetails>
      </Accordion>

      <Box height="10rem" />
    </SimpleShowLayout>
  );
};
